/**
 * Firestore Index service – all requests go through apiClient to platform-data backend.
 * Uses platform-data endpoints for Firestore index kinds and usage data.
 */

import { apiClient } from '../api/apiClient'
import { logger } from '../../utils/logger'

export type FirestoreEnv = 'staging' | 'production'

// Project IDs for different environments
const PROJECT_IDS: Record<FirestoreEnv, string> = {
  staging: 'highlevel-staging',
  production: 'highlevel-backend'
}

// Base URLs for environments
const BASE_URLS: Record<FirestoreEnv, string> = {
  staging: 'https://dev.services.leadconnectorhq.com',
  production: 'https://services.leadconnectorhq.com'
}

// Database lists per environment (from GCP Console screenshots)
const DATABASES: Record<FirestoreEnv, string[]> = {
  staging: [
    '(default)',
    'ai-staging',
    'automation',
    'calendars',
    'circuit-breaker',
    'conversations-staging',
    'cpaas-staging',
    'crm-leadconnector',
    'feature-permissions',
    'feature-permissions-staging',
    'highlevel-staging',
    'highlevel-vault',
    'highq',
    'integrations',
    'launchpad-staging',
    'lc-phone',
    'leadgen-funnel-staging',
    'leadgen-onboarding-staging',
    'leadgen-social-planner-staging',
    'locations',
    'locations-staging',
    'mongo-firestore-migration',
    'notifications',
    'platform',
    'platform-data',
    'platform-devtools',
    'platform-error-events',
    'quality-gates',
    'revex-internal-tools-staging',
    'revex-lc-email-staging',
    'revex-locations-staging',
    'revex-saas-staging',
    'saas-service',
    'test-db-terraform',
    'test-pipelines',
    'test-siddhant',
    'vector-search-trial',
    'wallet'
  ],
  production: [
    '(default)',
    'ai-production',
    'automation',
    'calendars',
    'circuit-breaker',
    'conversations-production',
    'crm-leadconnector',
    'feature-permissions',
    'highlevel-vault',
    'integrations',
    'internal-tools',
    'lc-phone',
    'leadgen-funnel-production',
    'leadgen-onboarding-production',
    'leadgen-social-planner-production',
    'notifications',
    'platform-error-events',
    'revex-lc-email-production',
    'revex-saas-production'
  ]
}

export interface FirestoreIndexProperty {
  name: string
  direction: 'ASCENDING' | 'DESCENDING'
}

export interface FirestoreIndexStat {
  kindName: string
  alphanumericId: string
  indexId: string
  properties: FirestoreIndexProperty[]
  ancestor: 'COLLECTION' | 'COLLECTION_GROUP'
  lastKnownUsageTime: string | null
  statTrackedSinceTime: string
}

export interface FirestoreIndexUsageSummary {
  total: number
  used: number
  unused: number
}

export interface FirestoreIndexUsageResponse {
  projectId: string
  databaseId: string
  collectionFilter: string
  type: string
  indexStats: FirestoreIndexStat[]
  summary: FirestoreIndexUsageSummary
  traceId: string
}

export interface FirestoreKindsResponse {
  kinds: string[]
}

function firestoreUrl(path: string, env: FirestoreEnv): string {
  const base = BASE_URLS[env]
  const url = new URL(path, base)
  return url.toString()
}

// Convert database display name to API value
// (default) -> empty string, others stay the same
function getDatabaseIdForApi(displayName: string): string {
  return displayName === '(default)' ? '' : displayName
}

export const firestoreService = {
  /**
   * Get the project ID for a given environment
   */
  getProjectId(env: FirestoreEnv): string {
    return PROJECT_IDS[env]
  },

  /**
   * Get list of databases for a given environment
   */
  getDatabases(env: FirestoreEnv): string[] {
    return DATABASES[env] || []
  },

  /**
   * Fetch kinds (collections) for a database.
   */
  async getKinds(
    databaseId: string,
    env: FirestoreEnv = 'production'
  ): Promise<FirestoreKindsResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = firestoreUrl('/platform-data/firestore-usage/kinds', env)

      const response = await apiClient.post<string[]>(url, {
        projectId,
        databaseId: getDatabaseIdForApi(databaseId)
      })

      return { kinds: response || [] }
    } catch (error) {
      logger.error('Failed to fetch Firestore kinds', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load collections. Please try again later.'
      throw new Error(message)
    }
  },

  /**
   * Fetch index usage for a collection (kind).
   * Only fetches composite indexes.
   */
  async getIndexUsage(
    databaseId: string,
    collectionFilter: string,
    env: FirestoreEnv = 'production'
  ): Promise<FirestoreIndexUsageResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = firestoreUrl('/platform-data/firestore-usage/usage', env)

      const response = await apiClient.post<FirestoreIndexUsageResponse>(url, {
        projectId,
        databaseId: getDatabaseIdForApi(databaseId),
        collectionFilter,
        type: 'composite'
      })

      return response
    } catch (error) {
      logger.error('Failed to fetch Firestore index usage', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load index usage. Please try again later.'
      throw new Error(message)
    }
  }
}
